import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FieldArrayRenderProps, useFormikContext } from "formik";
import { Info } from "@hitachivantara/uikit-react-icons";
import {
  HvGrid,
  HvTypography,
  HvGlobalActions,
  HvEmptyState,
  HvInput,
  HvTextArea,
} from "@hitachivantara/uikit-react-core";
import { Hidden } from "@material-ui/core";
import { SwitchWithLabels } from "components/form";
import { AttributeFormData } from "lib/api";
import { parseStatus as ps, parseStatusMessage as psm } from "lib/utils/forms";

import PropertyFileUpload from "./PropertyFileUpload";
import DateTime from "./DateTime";

import { useStyles } from "./styles";

type AttributeValue = string | boolean | Date | number | undefined;

const Properties: React.FC<FieldArrayRenderProps> = ({ replace }) => {
  const { t } = useTranslation("incidentAddEdit");
  const classes = useStyles();

  const {
    values: { attributes },
    errors,
    touched,
    status: { serverSideErrors } = {},
    setFieldTouched,
  } = useFormikContext<IncidentFormData>();

  const handleAddAttributeValue = useCallback(
    (attribute: AttributeFormData, value: AttributeValue) => {
      replace(attribute.order as number, {
        ...attribute,
        value,
      });
    },
    [replace]
  );

  const parseStatus = useCallback(
    (field: string) => ps(field, serverSideErrors, errors, touched),
    [errors, serverSideErrors, touched]
  );

  const parseStatusMessage = useCallback(
    (field: string) => {
      return psm(field, serverSideErrors, errors, touched);
    },
    [errors, serverSideErrors, touched]
  );

  const visibleAttributes = useMemo(
    () =>
      attributes ? attributes.filter((attr) => attr.appearance.visible) : [],
    [attributes]
  );

  return (
    <HvGrid container item xs={12} md={12}>
      <HvGrid sm={12} item>
        <HvGlobalActions title={t("properties.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("properties.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid xs={12} sm={12} item>
        {visibleAttributes.length > 0 ? (
          <HvGrid container justifyContent="flex-start">
            {visibleAttributes.map((attribute, index) => (
              <React.Fragment key={attribute.order}>
                {attribute.type !== "image" &&
                  attribute.contentType !== "image" &&
                  attribute.type !== "video" &&
                  attribute.contentType !== "video" && (
                    <>
                      <HvGrid item md={5} xs={12}>
                        {(attribute.type === "text" ||
                          attribute.type === "hyperlink" ||
                          attribute.type === "numeric") && (
                          <HvInput
                            name={`${attribute.label}-value`}
                            value={attribute.value?.toString() ?? ""}
                            label={attribute.label}
                            inputProps={{
                              autoComplete: "off",
                            }}
                            placeholder={t(
                              `properties.${attribute.type}Placeholder`
                            )}
                            onChange={(evt, value) => {
                              handleAddAttributeValue(
                                attribute,
                                value === "" && attribute.type === "numeric"
                                  ? undefined
                                  : value
                              );
                            }}
                            onBlur={() =>
                              setFieldTouched(
                                `attributes[${attribute.order}].value`
                              )
                            }
                            status={parseStatus(`attributes[${index}].value`)}
                            statusMessage={parseStatusMessage(
                              `attributes[${index}].value`
                            )}
                          />
                        )}
                        {attribute.type === "date and time" && (
                          <div className={classes.row}>
                            <DateTime
                              attribute={attribute}
                              onChange={(value) => {
                                handleAddAttributeValue(attribute, value);
                              }}
                            />
                          </div>
                        )}
                        {attribute.type === "boolean" && (
                          <SwitchWithLabels
                            name={`${attribute.label}-value`}
                            label={attribute.label}
                            offLabel={t("properties.hasPropertyLabel.no")}
                            onLabel={t("properties.hasPropertyLabel.yes")}
                            checked={attribute.value === true}
                            value={attribute.value ? "yes" : "no"}
                            onChange={(evt, value) => {
                              handleAddAttributeValue(attribute, value);
                            }}
                          />
                        )}
                        {attribute.type === "textarea" && (
                          <HvTextArea
                            name={`${attribute.label}-value`}
                            label={attribute.label}
                            value={attribute.value?.toString() ?? ""}
                            onChange={(evt, value) => {
                              handleAddAttributeValue(attribute, value);
                            }}
                            onBlur={() =>
                              setFieldTouched(
                                `attributes[${attribute.order}].value`
                              )
                            }
                            placeholder={t(
                              `properties.${attribute.type}Placeholder`
                            )}
                            rows={5}
                          />
                        )}
                      </HvGrid>
                      <Hidden smDown>
                        <HvGrid item md={9} role="presentation" />
                      </Hidden>
                    </>
                  )}

                {(attribute.type === "image" ||
                  attribute.contentType === "image" ||
                  attribute.type === "video" ||
                  attribute.contentType === "video") && (
                  <>
                    <HvGrid item md={7} xs={12} key={attribute.order}>
                      <PropertyFileUpload
                        attribute={attribute}
                        replace={replace}
                        index={index}
                      />
                    </HvGrid>
                    <Hidden smDown>
                      <HvGrid item md={5} role="presentation" />
                    </Hidden>
                  </>
                )}
              </React.Fragment>
            ))}
          </HvGrid>
        ) : (
          <HvEmptyState
            title={t("emptyState.title")}
            message={t("emptyState.description")}
            icon={<Info />}
          />
        )}
      </HvGrid>
    </HvGrid>
  );
};

export default Properties;
